import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { NoPermission } from './NoPermission';

interface RequirePermissionProps {
  /** Chave de permissão exigida (ex.: 'courts.view'). */
  permission: string;
  /** Módulo do clube que precisa estar habilitado (club_modules). */
  module?: string;
  children: ReactNode;
}

/**
 * Guard de rota do RBAC granular. Envolve cada `<Route>` em App.tsx (ex.:
 * `<RequirePermission permission="courts.view" module="courts">`).
 *
 * Regras (ver plano de RBAC, seção "Front CRM" / "Riscos"):
 *  - Sem clube ativo ainda (onboarding em andamento): passthrough — a
 *    própria página decide seu empty-state, igual hoje.
 *  - Owner do clube: sempre passthrough (bypass total da matriz).
 *  - Clube kind='professor': passthrough total — este modo tem nav e
 *    comportamento próprios (professorNavGroups), fora da matriz de
 *    permissões; um teacher convidado num espaço de professor hoje enxerga
 *    a MESMA nav completa do owner (restrita só por escopo row-level via
 *    os helpers professor_can_access_... / club_teacher_id_for) — aplicar
 *    a matriz aqui trancaria rotas (Agenda, Financeiro, Planos...) que ele
 *    já acessa, regressão que o invariante "modo professor permanece nav
 *    separada de produto" existe justamente para evitar.
 *  - Staff de clube tradicional: módulo desabilitado bloqueia (se
 *    informado); senão, exige `can(permission)`.
 */
export function RequirePermission({ permission, module, children }: RequirePermissionProps) {
  const { club, loading: authLoading } = useAuth();
  const { can, isOwner, moduleEnabled, isLoading: permissionsLoading } = usePermissions();

  if (authLoading || permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!club) return <>{children}</>;
  if (isOwner) return <>{children}</>;
  if (club.kind === 'professor') return <>{children}</>;

  if (module && !moduleEnabled(module)) return <NoPermission />;
  if (!can(permission)) return <NoPermission />;

  return <>{children}</>;
}
